import {
  AssistantPhoto,
  BookmarkAdded,
  Delete,
  DirectionsRun,
  Done,
  EditCalendar,
  Flag,
  FmdGoodOutlined,
  KeyboardArrowUp,
  OpenInNewRounded,
  PersonOutlineOutlined,
  PhoneOutlined,
  StorefrontOutlined
} from "@mui/icons-material";
import { Collapse, Divider, Stack, Typography } from "@mui/material";
import CustomButton from "components/CustomButton";
import CustomCard from "components/CustomCard";
import CustomMenu from "components/CustomMenu";
import NullCheck from "components/NullCheck";
import { BIZ_TYPE, DATE_TIME_FORMAT } from "config/const";
import { currencyFormat, lineText, listTolineText } from "config/functions";
import { ROLES_ID } from "config/roles";
import { ST_TYPE_ID } from "containers/servicesCall/const";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const Details = ({
  user,
  itemList,
  call,
  openService,
  setSelectEmp,
  deleteServicesCall,
  associatedEmp,
  setOpenFinishService,
  editService,
  now,
  createGoogleCalendar
}) => {
  const { bt } = useBizTypeTranslation();
  const closeBtnCountingRef = useRef(0);
  const biz = useSelector((state) => state?.biz?.data);
  const [closeBtnCounting, setCloseBtnCounting] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [dropList, setDropList] = useState([])

  const isOutOfTime = call?.workDt &&
    call?.stTypeId === ST_TYPE_ID.OPEN &&
    moment(call?.workDt).isBefore(moment(), 'day')

  const MENU_OPTIONS = [
    {
      title: call?.hasFlag ? "REMOVE_HAS_FALG" : "ADD_HAS_FALG",
      icon: <AssistantPhoto />,
      show: true,
      onClick: () => editService({ call: call, hasFlag: call?.hasFlag ? false : true }),
    },
    {
      title: "CHANGE_EMP",
      icon: <DirectionsRun />,
      show: user?.userRole !== ROLES_ID.EMPLOYEE,
      onClick: () => setSelectEmp({ open: true, call: call }),
    },
    {
      title: "CREATE_GOOGLE_CALENDAR",
      icon: <EditCalendar />,
      show: true,
      onClick: () => createGoogleCalendar(call),
    },
    {
      title: "CLOSE_SERVICE",
      icon: <BookmarkAdded />,
      show: true,
      onClick: () => setOpenFinishService({ open: true, call: call }),
    },
    {
      title: "DELETE",
      icon: <Delete color="error" />,
      show: user?.userRole !== ROLES_ID.EMPLOYEE && biz?.type === BIZ_TYPE.MAINTENANCE_DEPARTMENT,
      onClick: () => deleteServicesCall(call?._id, call?.callId),
    },
  ];

  useEffect(() => {
    if (!call?.workDt || call?.stTypeId !== ST_TYPE_ID.OPEN)
      return setElapsedTime(null)

    const workTime = moment(call.workDt);

    if (now.isBefore(workTime))
      return setElapsedTime(null)

    const diff = moment.duration(moment().diff(workTime));

    const days = String(diff.days()).padStart(2, '0');
    const hours = String(diff.hours()).padStart(2, '0');
    const minutes = String(diff.minutes()).padStart(2, '0');
    // const seconds = String(diff.seconds()).padStart(2, '0');

    const elapsedTimeString = [
      // `${seconds} ${bt("SECONDS")} ${diff.minutes() > 0 ? "|" : ""}`,

      diff.minutes() > 0 || diff.hours() > 0
        ? `${minutes} ${bt('MINUTES')} ${diff.hours() > 0 || diff.days() > 0 ? "|" : ""}`
        : "",

      diff.hours() > 0 || diff.days() > 0
        ? `${hours} ${bt("HOURS")} ${diff.days() > 0 ? "|" : ""}`
        : "",

      diff.days() > 0 ? `${days} ${bt("DAYS")}` : "",
    ]
      .filter(Boolean)
      .join(" ");

    setElapsedTime(elapsedTimeString);
  }, [call, now]);

  const onClickDrop = (id) => {
    if (dropList.includes(id))
      setDropList(prav => prav.filter(i => i !== id))
    else
      setDropList(prav => ([...prav, id]))
  }

  const Item = ({ title, icon, value, fontSize, column, isLineText }) => {
    return (
      <NullCheck valueForCheck={value && itemList.includes(column)}>
        <Stack direction="row" gap={1} >

          <NullCheck valueForCheck={title}>
            <Typography fontSize={fontSize ?? 14} fontWeight="bold">
              {`${bt(title)}:`}
            </Typography>
          </NullCheck>

          <NullCheck valueForCheck={icon}>
            <Typography fontSize={fontSize ?? 14} fontWeight="bold">
              {icon}
            </Typography>
          </NullCheck>

          {!isLineText
            ? <Typography
              sx={{ maxWidth: 150, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", }}
              fontSize={fontSize ?? 14}
            >
              {value}
            </Typography>
            : <Stack>{value}</Stack>
          }
        </Stack>
      </NullCheck>
    );
  };

  const onClickCloseButton = (event) => {
    event.stopPropagation();
    closeBtnCountingRef.current += 1;
    setCloseBtnCounting(prev => prev + 1)
    if (closeBtnCountingRef.current === 2) {
      setOpenFinishService({ open: true, call: call })
      closeBtnCountingRef.current = 0;
      setCloseBtnCounting(0)
    }

    setTimeout(() => {
      closeBtnCountingRef.current = 0;
      setCloseBtnCounting(0)
    }, 400);
  };

  const onClick = (event) => {
    event.stopPropagation();
    openService(call)
  }

  return (
    <CustomCard
      height="100%"
      spacing={2}
      padding={0}
      justifyContent="space-between"
      width={{ xs: "100%", md: 350 }}
      sx={{
        alignSelf: "flex-start",
        height: "auto",
        bgcolor: call?.color || 'bgPaper.main',
        border: !isOutOfTime && call?.isRead ? 0 : 0.5,
        borderColor: isOutOfTime
          ? 'error.text'
          : !call?.isRead
            ? 'success.main'
            : 'primary.text'
      }}
    >
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent='space-between'
          minHeight={35}
        >
          <Stack direction='row' px={1}>
            <NullCheck valueForCheck={call?.hasFlag}>
              <Flag sx={{ color: "warning.main" }} />
            </NullCheck>

            <Typography paddingX={1} color="primary.text">
              {`${bt("SERVICES_CALL")} - ${call.callId}`}
            </Typography>
          </Stack>

          <CustomMenu iconColor='primary.text' options={MENU_OPTIONS} />
        </Stack>

        <Stack padding={1} spacing={1} onClick={() => onClickDrop(call?.id)}>
          <Stack direction="row" spacing={2}>
            <Item
              icon={<PersonOutlineOutlined fontSize="small" sx={{ color: "primary.main" }} />}
              column="name"
              value={call?.name}
            />

            <Item
              icon={<PhoneOutlined fontSize="small" sx={{ color: "primary.main" }} />}
              column="tel"
              value={call?.tel}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <Item
              icon={<StorefrontOutlined fontSize="small" sx={{ color: "primary.main" }} />}
              column="company"
              value={call?.clienCompany ?? ""}
            />

            <Item
              icon={<FmdGoodOutlined fontSize="small" sx={{ color: "primary.main" }} />}
              column="addr"
              value={call?.addr}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2}>

              <CustomButton
                startIcon={<OpenInNewRounded
                  sx={{ color: 'success.text' }}
                />}
                title="OPEN"
                width={100}
                height={40}
                color={'success.light'}
                textColor={'success.text'}
                onClick={onClick}
              />

              <CustomButton
                startIcon={<Done
                  sx={{ color: 'success.text' }}
                />}
                title="CLOSE_SERVICE"
                height={40}
                color={closeBtnCounting === 1 ? 'error.text' : "primary.text"}
                textColor={'success.text'}
                onClick={onClickCloseButton}
              />
            </Stack>

            <KeyboardArrowUp
              sx={{
                fontSize: 25,
                transform: dropList.includes(call.id) ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Stack>
        </Stack>

        <Collapse in={dropList.includes(call?.id)}>
          <Stack borderTop={0} borderColor="bgDivider.main" p={1} spacing={1}>
            <Item
              title="CLIENT_CALL_ID"
              column="clientCallId"
              value={call?.clientCallId}
            />

            <Item
              title="PRICE"
              column="price"
              value={currencyFormat(call?.price || 0)}
            />
            <Item
              title="PROVIDERS_EXPENSES"
              column="providersExpenses"
              value={currencyFormat(call?.providersExpenses || 0)}
            />
            <Item
              title="CREATED"
              column="created"
              value={call?.created ? moment(call?.created).format(DATE_TIME_FORMAT) : null}
            />
            <Item
              title="WORK_DT"
              column="workDt"
              value={call?.workDt ? moment(call?.workDt).format(DATE_TIME_FORMAT) : null}
            />
            <Item
              title="SELECT_EMPLOYEE"
              column="associatedEmp"
              value={associatedEmp ? `${associatedEmp?.fn} ${associatedEmp?.ln}` : null}
            />
            <Item
              title="NOTES"
              column="notes"
              value={lineText(call?.notes)}
              isLineText
            />

            <Item
              title="PROVIDERS"
              column="providers"
              value={lineText(call?.providers)}
              isLineText
            />


            <NullCheck valueForCheck={itemList?.includes("contentWithItems")}>
              <Divider sx={{ bgcolor: "primary.main" }} />
              <Stack>
                {listTolineText(call?.contentWithItems)}
              </Stack>
            </NullCheck>
          </Stack>
        </Collapse>
      </Stack>

      <Stack
        spacing={0.5}
        height={30}
        px={2}
        pt={1}
        borderTop={1}
        direction="row"
        borderColor="bgDivider.main"
        alignItems="center"
        justifyContent="space-between"
      >
        <NullCheck valueForCheck={elapsedTime}>
          <Typography variant="body2" >
            {bt("TIME_FROM_STARTCALL")}
          </Typography>
          <Typography variant="body2" >
            {elapsedTime}
          </Typography>
        </NullCheck>
      </Stack>
    </CustomCard >
  );
};

export default Details;